<template>
  <div>
    <ConfirmationModal
      v-if="showConfirmationModal"
      @submit="download"
      @close="showConfirmationModal = false"
    />
    <Flex
      direction="column"
      :padding=".5"
    >
      <BaseDropdown
        v-model="dataType"
        :options="dataTypes"
        :label="$t('export_page.report_information')"
        v-test="'export-type'"
      />
      <BaseDropdown
        v-if="isMonthly"
        v-model="month"
        :label="$t('export_page.month')"
        :options="monthItems"
        v-test="'export-month'"
      />
      <BaseRadio
        v-if="multiLocation"
        v-model="selectedDataScope"
        :disabled="disableLocationSelect || forceLocationSelect"
        :options="[{ label: $t('global.entire_company'), value: 'GLOBAL' }, { label: filters.capitalize($t('global.items.location', 1)), value: 'LOCAL' }]"
        :label="$t('export_page.export_for')"
        :tooltip="disableLocationSelect ? $t('export_page.location_select_disabled_tooltip') : (forceLocationSelect ? $t('export_page.location_select_disabled_tooltip_2') : null)"
        v-test="'export-data-scope'"
      />
      <BaseDropdown
        v-if="multiLocation && selectedDataScope === 'LOCAL'"
        v-model="selectedLocationId"
        :disabled="disableLocationSelect"
        :options="locations.map((location) => ({ label: location.internalName, value: location.id }))"
        :label="filters.capitalize($t('global.items.location', 1))"
        v-test="'export-location'"
      />
      <BaseRadio
        v-if="dataType === 'customers'"
        v-model="enrichedData"
        :options="[
          { 
            label: $t('export_page.basic_data'), 
            value: false
          }, 
          { 
            label: $t('export_page.all_data'), 
            value: true
          }
        ]"
        :label="$t('export_page.include')"
        v-test="'export-enriched-data'"
      />
      <BaseAlert
        v-if="dataType === 'customers' && enrichedData"
        :text="$t('export_page.enriched_data_warning')"
        color="warning"
        v-test="'export-enriched-data-warning'"
      />
      <BaseRadio
        v-if="dataFormats.length > 1"
        v-model="dataFormat"
        :label="$t('export_page.file_type')"
        :options="dataFormats"
        buttons
        v-test="'export-format'"
      />
      <Flex justify="end">
        <BaseButton
          :loading="isSaving"
          @click="hasAsyncExports ? (showConfirmationModal = true) : download()"
          v-test="'export-btn-download'"
        >
          {{ $t('global.actions.export') }}
        </BaseButton>
      </Flex>
      <Flex direction="column">
        <BaseAlert
          v-if="remoteExportScheduled"
          color="info"
          :text="`${$t('export_page.remote_export_scheduled', { amount: remoteExportScheduled }, remoteExportScheduled)} ${$t('export_page.remote_export_scheduled_2')}`"
          v-test="'export-remote-export-scheduled'"
        />
        <BaseAlert
          v-if="alreadyScheduledError"
          color="warning"
          :text="$t('export_page.remote_export_already_scheduled')"
          v-test="'export-remote-export-already-scheduled'"
        />
      </Flex>
    </Flex>
  </div>
</template>

<script lang="ts">
import gql from 'graphql-tag';
import { mapState } from 'pinia';
import { defineComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import config from '@/config';
import dayjs from '@/dayjs';
import eventBus from '@/event-bus';
import filters from '@/filters';
import { flash, modal } from '@/helpers/ui';
import { useCompanyStore } from '@/stores/company';
import { useLocationsStore } from '@/stores/locations';
import { useUserStore } from '@/stores/user';
import unleash from '@/unleash';
import ConfirmationModal from './ConfirmationModal.vue';
import RemoteExportList from './RemoteExportList.vue';

export default defineComponent({
  components: { ConfirmationModal, RemoteExportList },
  props: {
    heading: {
      type: String,
    },
    months: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    dataType: {
      type: String,
      required: false,
    },
    fileFormat: {
      type: String,
      required: false,
    },
  },
  setup(props) {
    const { locationId } = useLocationsStore();
    const { multiLocation } = useCompanyStore();
    const selectedDataScope = ref('GLOBAL');
    const selectedLocationId = ref(locationId);
    const remoteExportScheduled = ref(0);
    const { t } = useI18n();

    const dataType = ref(props.dataType || null);
    const dataFormat = ref(props.fileFormat || 'xls');

    eventBus.$on('export-completed', () => {
      remoteExportScheduled.value = 0;
      flash(t('global.flash.export_completed'));
    });

    return {
      filters,
      selectedDataScope,
      selectedLocationId,
      multiLocation,
      remoteExportScheduled,
      dataType,
      dataFormat,
    };
  },
  data() {
    return {
      month: null,
      isSaving: false,
      lastExport: dayjs.unix(0),
      lastExportType: '',
      showConfirmationModal: false,
      alreadyScheduledError: false,
      enrichedData: false,
    };
  },
  watch: {
    dataFormats: {
      handler(value) {
        // When selecting a different dataType, the selected dataFormat might not be available
        // If so, select the first format
        const values = value.map((item) => item.value);
        if (values.length && !values.includes(this.dataFormat)) {
          this.dataFormat = values[0];
        }
      },
      immediate: true,
      deep: true,
    },
    selectedDataScope: {
      handler() {
        this.resetExportTimer();
      },
    },
    selectedLocationId: {
      handler() {
        this.resetExportTimer();
      },
    },
    dataType: {
      handler() {
        this.resetExportTimer();
        if (this.disableLocationSelect) {
          this.selectedDataScope = 'GLOBAL';

          const { locationId } = useLocationsStore();
          this.selectedLocationId = locationId;
        } else if (this.forceLocationSelect) {
          this.selectedDataScope = 'LOCAL';
        }
      },
    },
    dataFormat: {
      handler() {
        this.resetExportTimer();
      },
    },
    month: {
      handler() {
        this.resetExportTimer();
      },
    },
  },
  computed: {
    ...mapState(useUserStore, ['hasFeatureFlag']),
    ...mapState(useCompanyStore, ['company', 'multiLocation']),
    ...mapState(useLocationsStore, ['location', 'locations']),
    isMonthly() {
      return this.months.length;
    },
    hasAsyncExports() {
      return unleash.isEnabled('AsyncExports') && !this.hasRemoteAsyncExports;
    },
    hasRemoteAsyncExports() {
      return (
        unleash.isEnabled('NewAsyncExports') &&
        (!this.isMonthly ||
          this.dataType === 'appointments' ||
          this.dataType === 'tips')
      );
    },
    dataFormats() {
      return [
        {
          label: 'XLS',
          value: 'xls',
          disabled: this.dataType === 'datev' || this.dataType === 'gobd',
        },
        {
          label: 'CSV',
          value: 'csv',
          disabled: this.dataType === 'gobd',
        },
        {
          label: 'ZIP',
          value: 'zip',
          disabled: this.dataType !== 'gobd',
        },
      ].filter((item) => !item.disabled);
    },
    monthItems() {
      return this.months.map((month) => ({
        value: month,
        label: this.filters.date(month, { format: 'monthYear' }),
      }));
    },
    dataTypes() {
      const lifetimeItems = [
        {
          value: 'appointments',
          label: this.$t('global.items.appointment', 2),
          disabled: !this.hasFeatureFlag('module-appointments'),
        },
        {
          value: 'absences',
          label: this.$t('global.items.absence', 2),
        },
        {
          value: 'customers',
          label: this.$t('global.items.customer', 2),
        },
        {
          value: 'products',
          label: this.$t('global.items.product', 2),
          disabled: !this.hasFeatureFlag('module-register'),
        },
        {
          value: 'current_product_stock',
          label: this.$t('export_page.data_types.current_product_stock'),
          disabled: !this.hasFeatureFlag('module-register'),
        },
        {
          value: 'treatment_reports',
          label: this.$t('global.items.treatment_report', 2),
        },
        {
          value: 'tips',
          label: this.$t('reports.totals.pos_tips'),
          disabled: !this.hasFeatureFlag('module-register'),
        },
      ];
      if (this.company.country !== 'be') {
        lifetimeItems.push({
          value: 'deleted_appointments',
          label: this.$t('export_page.data_types.deleted_appointments'),
          disabled: !this.hasFeatureFlag('module-appointments'),
        });
      }
      const montlyItems = [
        {
          value: 'day_totals',
          label: this.$t('export_page.data_types.day_totals'),
          disabled: !this.hasFeatureFlag('module-register'),
        },
        {
          value: 'orders',
          label: this.$t('global.items.order', 2),
          disabled: !this.hasFeatureFlag('module-register'),
        },
        {
          value: 'order_items',
          label: this.$t('export_page.data_types.order_items'),
          disabled: !this.hasFeatureFlag('module-register'),
        },
        {
          value: 'drawer_transactions',
          label: this.$t('export_page.data_types.drawer_transactions'),
          disabled: !this.hasFeatureFlag('module-register'),
        },
        {
          value: 'worked_hours',
          label: this.$t('export_page.data_types.worked_hours'),
        },
        {
          value: 'appointments',
          label: this.$t('global.items.appointment', 2),
          disabled:
            !this.hasFeatureFlag('module-appointments') &&
            !this.hasFeatureFlag('module-register'),
        },
        {
          value: 'datev',
          label: 'Datev',
          disabled:
            !this.hasFeatureFlag('admin-export-datev') &&
            !this.hasFeatureFlag('module-register'),
        },
        {
          value: 'gobd',
          label: 'GoBD',
          disabled:
            !this.hasFeatureFlag('admin-export-gobd') &&
            !this.hasFeatureFlag('module-register'),
        },
        {
          value: 'tips',
          label: this.$t('reports.totals.pos_tips'),
          disabled: !this.hasFeatureFlag('module-register'), // TODO: show only when terminals enabled?
        },
      ];
      const items = !this.isMonthly ? lifetimeItems : montlyItems;
      return items
        .filter((item) => !item.disabled)
        .map((item) => ({
          value: item.value,
          label: this.filters.capitalize(item.label),
        }))
        .sort((a, b) =>
          a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
        );
    },
    disableLocationSelect() {
      return (
        !['appointments', 'tips', 'deleted_appointments'].includes(
          this.dataType,
        ) && !this.forceLocationSelect
      );
    },
    forceLocationSelect() {
      return this.dataType === 'worked_hours';
    },
  },
  methods: {
    resetExportTimer() {
      this.lastExport = dayjs.unix(0);
      this.lastExportType = '';
    },
    asyncExport(email?: string) {
      if (
        dayjs().diff(dayjs(this.lastExport), 'minute') < 5 &&
        this.lastExportType === this.dataFormat
      ) {
        modal('warning', {
          message: this.$t('customers.export_warning.message'),
          subMessage: this.$t('customers.export_warning.subMessage', {
            email: this.company.notificationEmail,
          }),
        });
        return;
      }
      this.isSaving = true;
      this.lastExport = dayjs();
      this.lastExportType = this.dataFormat;

      return new Promise((resolve) => {
        this.$apollo
          .mutate({
            mutation: gql`
              mutation createExport($input: CreateExportInput!) {
                createExport(input: $input) {
                  status
                }
              }
            `,
            variables: {
              input: {
                locationId: this.selectedLocationId,
                dataScope: this.selectedDataScope,
                exportType: this.dataType ? this.dataType.toUpperCase() : null,
                fileFormat: this.dataFormat
                  ? this.dataFormat.toUpperCase()
                  : null,
                month: this.month,
                recipientEmail: email,
              },
            },
          })
          .then((response) => {
            flash(this.$t('global.flash.export_mailed'));
            resolve(response.data.status);
          })
          .finally(() => {
            this.isSaving = false;
          });
      });
    },
    newAsyncExport() {
      this.isSaving = true;
      this.alreadyScheduledError = false;

      let dataType = this.dataType;

      if (this.dataType === 'customers' && this.enrichedData) {
        dataType = 'customers_enriched';
      }

      this.$apollo
        .mutate({
          mutation: gql`
            mutation createRemoteExport($input: CreateRemoteExportInput!) {
              createRemoteExport(input: $input) {
                status
              }
            }
          `,
          variables: {
            input: {
              locationId: this.selectedLocationId,
              dataScope: this.selectedDataScope,
              exportType: dataType ? dataType.toUpperCase() : null,
              fileFormat: this.dataFormat
                ? this.dataFormat.toUpperCase()
                : null,
              month: this.isMonthly ? dayjs(this.month).month() + 1 : null, // month is 1-indexed in the backend, but dayjs is 0-indexed
              year: this.isMonthly ? dayjs(this.month).year() : null,
            },
          },
        })
        .then((response) => {
          if (response.data.createRemoteExport.status === 'scheduled') {
            flash(this.$t('global.flash.export_scheduled'));
            this.remoteExportScheduled++;
          } else if (
            response.data.createRemoteExport.status === 'already_scheduled'
          ) {
            this.alreadyScheduledError = true;
          }
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    download(email?: string) {
      if (this.hasRemoteAsyncExports) {
        this.newAsyncExport();
      } else if (this.hasAsyncExports) {
        this.asyncExport(email);
      } else {
        let url;
        const year = dayjs(this.month).year();
        const parentScope =
          this.selectedDataScope === 'GLOBAL' ? 'company' : 'location';
        if (this.isMonthly) {
          url = `${config.backendUrl}/api/exports/${this.dataType}.${this.dataFormat}?month=${this.month}&year=${year}&location_id=${this.selectedLocationId}&parent_scope=${parentScope}`;
        } else {
          url = `${config.backendUrl}/api/exports/${this.dataType}.${this.dataFormat}?parent_scope=${parentScope}&location_id=${this.selectedLocationId}`;
        }
        window.open(url);
      }
    },
  },
  created() {
    if (this.isMonthly) {
      this.month = this.months[0];
    }
    if (this.dataTypes.length && !this.dataType) {
      this.dataType = this.dataTypes[0].value;
    }
  },
});
</script>
